import { Box, Button, Icon, List, Text } from '@factorialco/deprecated-design-system'

import LabeledInput from 'modules/core/components/LabeledInput'
import TextArea from 'modules/core/components/TextArea'
import { useForm } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import DropZoneBox from 'modules/expenses/components/common/DropZone'
import { GroupNameField } from 'modules/expenses/components/expenses/New/GroupModal'
import { Group } from 'modules/expenses/hooks/useGroup'

import { EditGroupFormValues, GroupFile } from './types'

interface FormProps {
  group: Group
  form: ReturnType<typeof useForm<EditGroupFormValues>>
  canEdit: boolean
}

interface FilesListProps {
  files: GroupFile[]
  readonly: boolean
  onDeleteFile: (file: GroupFile) => void
}

interface DownloadFileProps {
  file: GroupFile
}

const DownloadFile = ({ file }: DownloadFileProps) => {
  const url = file instanceof File ? URL.createObjectURL(file) : file.downloadUrl || file.url
  if (!url) return null

  return (
    <Button.Router
      type="secondary"
      to={url}
      blankTarget
      rel="noopener noreferrer"
      download={file.name}
      iconName="download"
    />
  )
}

const FilesList = ({ files, readonly, onDeleteFile }: FilesListProps) => {
  if (files.length === 0) return null

  return (
    <List>
      {files.map((file) => (
        <List.Item key={file.name + file.size}>
          <Box gap="s8" flexDirection="row" alignItems="center">
            <Icon.Medium icon="file-filled" color="grey800" />
            <Box maxWidth="s240">
              <List.Title ellipsis>{file.name}</List.Title>
            </Box>
          </Box>
          <Box gap="s8" flexDirection="row" alignItems="center">
            <DownloadFile file={file} />
            {!readonly && (
              <Button type="secondary" onClick={() => onDeleteFile(file)} iconName="delete" />
            )}
          </Box>
        </List.Item>
      ))}
    </List>
  )
}

export const Form = ({ group, form, canEdit }: FormProps) => {
  const onDeleteFile = (file: GroupFile) => {
    form.setFieldValue(
      'files',
      form.values.files.filter((f) => f !== file)
    )

    if (file.id) {
      form.setFieldValue('filesIdToRemove', [...form.values.filesIdToRemove, file.id])
    }
  }

  return (
    <Box gap="s24">
      <GroupNameField
        value={form.values.name}
        error={form.touched.name ? form.errors.name : undefined}
        readonly={!canEdit}
        onChange={(value) => form.setFieldValue('name', value)}
      />
      <TextArea
        label={i18n.t('expenses_v2.forms.fields.expensable_group.description.label')}
        placeholder={i18n.t('expenses_v2.forms.fields.expensable_group.description.placeholder')}
        value={form.values.description}
        error={form.touched.description ? form.errors.description : undefined}
        readonly={!canEdit}
        onChange={(value) => form.setFieldValue('description', value)}
      />
      <LabeledInput
        isLegend
        label={i18n.t('expenses_v2.forms.fields.expensable_group.files.label')}
        readonly={!canEdit}
      >
        <Box gap="s24">
          <Text.Normal weight="medium" size="200">
            {i18n.t('expenses_v2.forms.fields.expensable_group.files.description')}
          </Text.Normal>
          {canEdit && (
            <Box maxHeight="s240">
              <DropZoneBox
                onDrop={(files: GroupFile[]) =>
                  form.setFieldValue('files', [...form.values.files, ...files])
                }
                title={i18n.t('expenses_v2.forms.fields.dropzone.title')}
                description={i18n.t('expenses_v2.forms.fields.dropzone.description')}
                multiple
              />
            </Box>
          )}
          <FilesList files={form.values.files} readonly={!canEdit} onDeleteFile={onDeleteFile} />
        </Box>
      </LabeledInput>
    </Box>
  )
}
